import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { dbLogin, dbFetchUsers, dbSaveUser, dbDeleteUser, dbLogSession, dbKeepAlive } from "./supabase";

export const COLORS = ["#1D9E75","#534AB7","#D85A30","#BA7517","#185FA5","#086375","#6B4226","#A32D2D"];

const AuthContext = createContext(null);

export const isAccessValid = (user) => {
  if (!user.active) return false;
  if (user.accessExpiry && new Date(user.accessExpiry) < new Date()) return false;
  return true;
};

export function AuthProvider({ children }) {
  const [user,       setUser]       = useState(null);
  const [users,      setUsersState] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);

  // ── Restore session from localStorage on mount ────────────────────────────
  useEffect(() => {
    const raw = localStorage.getItem("mealbox_session");
    if (raw) {
      try { setUser(JSON.parse(raw)); } catch { localStorage.removeItem("mealbox_session"); }
    }
  }, []);

  // ── Load all users from Supabase on mount ─────────────────────────────────
  useEffect(() => {
    dbFetchUsers()
      .then(data => { setUsersState(data); setLoadingUsers(false); })
      .catch(() => setLoadingUsers(false));
  }, []);

  // ── setUsers — saves to Supabase and updates local state ──────────────────
  const setUsers = useCallback(async (updaterOrUser) => {
    // Accepts either a function (like setState) or a direct user object to upsert
    if (typeof updaterOrUser === "function") {
      setUsersState(prev => {
        const next = updaterOrUser(prev);
        // Sync each changed user to Supabase
        next.forEach(u => {
          const old = prev.find(p => p.id === u.id);
          if (!old || JSON.stringify(old) !== JSON.stringify(u)) dbSaveUser(u).catch(console.error);
        });
        return next;
      });
    } else {
      // Single user upsert
      const saved = await dbSaveUser(updaterOrUser);
      setUsersState(prev => prev.find(u => u.id === saved.id) ? prev.map(u => u.id === saved.id ? saved : u) : [...prev, saved]);
    }
  }, []);

  const removeUser = useCallback(async (id) => {
    await dbDeleteUser(id);
    setUsersState(prev => prev.filter(u => u.id !== id));
  }, []);

  // ── Login ─────────────────────────────────────────────────────────────────
  const login = useCallback(async (loginId, password) => {
    const result = await dbLogin(loginId, password);
    if (!result.success) return result;
    setUser(result.user);
    localStorage.setItem("mealbox_session", JSON.stringify(result.user));
    dbLogSession(result.user, "login").catch(() => {});
    dbKeepAlive(); // prevent free tier pausing
    return result;
  }, []);

  // ── Logout ────────────────────────────────────────────────────────────────
  const logout = useCallback((currentUser) => {
    const u = currentUser || JSON.parse(localStorage.getItem("mealbox_session") || "null");
    if (u) dbLogSession(u, "logout").catch(() => {});
    setUser(null);
    localStorage.removeItem("mealbox_session");
  }, []);

  const isSuperAdmin  = user?.id === "superadmin";
  const providerUsers = users.filter(u => u.id !== "superadmin");

  return (
    <AuthContext.Provider value={{ user, users, providerUsers, setUsers, removeUser, login, logout, isSuperAdmin, loadingUsers }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
